/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from "react";
import { bool, shape, string } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { compose } from "redux";
import { useSelector, connect } from "react-redux";
import * as Yup from "yup";
import * as i18nUtils from "util/i18n";
import { isEmpty } from "lodash";
import { withFormik, Form, Field } from "formik";

import { actions as formActions, selectors as formSelectors } from "reducers/form";

import { useTransaction } from "hooks/useTransactions";
import { actions as afipPaymentLinkActions } from "reducers/afipPaymentLink";
import Button from "pages/_components/Button";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import useReactPrint from "pages/_components/Print/useReactToPrint";

import CustomData from "pages/_components/CustomData/CustomData";
import GridTable from "pages/_components/GridTable/GridTable";
import CustomText from "pages/_components/CustomText/CustomText";
import Credential from "pages/_components/fields/credentials/Credential";

import FormattedAmount from "pages/_components/FormattedAmount";
import InfoTag from "pages/echeck/components/InfoTag";
import { transactionStatus } from "pages/transactions/constants";
import I18n from "pages/_components/I18n";
import { TransactionCard, TransactionHeader, TransactionSign } from "pages/_components/TransactionCard";

const VepPaymentTransactionPage = ({ isDesktop, isCancellingTransaction, fetchingCancel }) => {
    const [vepList, setVepList] = useState([]);

    const formatTransactionPayment = (data, idTransaction) => ({ ...data, idTransaction });

    const { isLoading, transaction, dispatch, handleSignTransaction, push, id, status } = useTransaction(
        `/vepPayments/newPayment/confirm`,
        afipPaymentLinkActions.setVepPaymentData,
        null,
        formatTransactionPayment,
    );
    const { transactionLines } = useSelector((state) => state.form);

    // eslint-disable-next-line no-unused-vars
    const getTrxLineExtendedData = (extendedData, trxLineStatus) => {
        if (extendedData !== null) {
            try {
                const parsedObj = JSON.parse(extendedData);
                return parsedObj || null;
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    const { user } = useSelector((_state) => _state.session);

    const showSignButton =
        isEmpty(transaction?.signatures?.find((sign) => sign.idUser === user?.userId)) &&
        transaction.idTransactionStatus === transactionStatus.PENDING;

    const formatTrxLines = (lines) =>
        lines.map((line) => {
            const extendedData = getTrxLineExtendedData(line?.extendedData, line?.status);
            const data = {
                id: extendedData?.id || "-",
                expirationDate: extendedData?.expirationDate || "-",
                cuitTaxpayer: extendedData?.cuitTaxpayer || "-",
                description: extendedData?.description || "-",
                currency: extendedData?.currency || "-",
                amount: extendedData?.amount || "-",
                status: extendedData?.status || "-",
                sequenceNumber: extendedData?.sequenceNumber || "-",
            };
            return data;
        });

    const getValidLines = (lines) => lines.filter((line) => line?.idTransaction === id);

    useEffect(() => {
        if (vepList === undefined || vepList.length === 0) {
            if (
                transaction?.idTransactionStatus === transactionStatus.PENDING ||
                transaction?.idTransactionStatus === transactionStatus.CANCELLED
            ) {
                setVepList(transaction?.data.vepList || []);
            } else {
                const list = formatTrxLines(getValidLines(transactionLines?.list) || []);
                setVepList(list);
            }
        }
    }, [transaction, transactionLines]);

    const { isFlowComplete, isProcessing, isBackoffice } = status;
    const [showMoreData, setShowMoreData] = useState(!isDesktop);
    const { componentRef, handlePrint, mobileRef, handleShareMobile } = useReactPrint(
        "vep.new.payment.title",
        id,
        "vepPaymentTransactionPage",
    );

    const handleDownload = async () => {
        await setShowMoreData(true);
        return handlePrint();
    };

    const backLinkTo = isFlowComplete ? "/lotPayments" : "";

    const tagType =
        // eslint-disable-next-line no-nested-ternary
        transaction.idTransactionStatus === transactionStatus.FINISHED
            ? "tag-success"
            : transaction.idTransactionStatus === transactionStatus.FAILED ||
              transaction.idTransactionStatus === transactionStatus.CANCELLED
            ? "tag-error"
            : "tag-warning";

    const toggleShowMore = () => {
        setShowMoreData((prev) => !prev);
    };

    const isPendingTransaction =
        transaction?.idTransactionStatus === transactionStatus.PROCESSING ||
        transaction?.idTransactionStatus === transactionStatus.PENDING;

    return (
        <>
            <Notification scopeToShow="vepPaymentTransactionPage" />
            {!isBackoffice && (
                <ResponsiveHeader
                    ticket
                    id="vep.new.payment.title"
                    metaTitle="vep.new.payment.title"
                    backLinkTo={backLinkTo}
                />
            )}
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            <div className="container transaction-ticket">
                                <div className="ticket__wrapper">
                                    <TransactionHeader
                                        title="vep.new.payment.title"
                                        subTitle={isProcessing ? "forms.transaction.ticket.status.PROCESSING" : ""}
                                    />
                                    <div className="ticket">
                                        <div className="ticket__header" />
                                        <div className="ticket__body refills-transaction">
                                            <div className="ticket-header-info">
                                                <div className="vep-ticket-info">
                                                    <CustomData
                                                        titleText="vep.payment.cuitCreator"
                                                        titleTextType="I18N"
                                                        valueText={transaction?.data?.cuitCreator || "-"}
                                                        valueTextType="TEXT"
                                                    />
                                                    <div className={isDesktop ? "px-6" : "py-6"} />
                                                    <CustomData
                                                        dataClass={`amount tag tag-echeq-character" ${tagType}`}
                                                        titleText={i18nUtils.get(
                                                            "vep.payment.total.amount",
                                                            "Monto total",
                                                        )}
                                                        valueText={
                                                            <FormattedAmount
                                                                currency="ARS"
                                                                quantity={
                                                                    transaction?.data?.amount?.quantity ||
                                                                    transaction?.data?.amount ||
                                                                    transaction?.data?.totalAmount ||
                                                                    0
                                                                }
                                                            />
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <div className="view-more-data-button min-width-full-container">
                                                <div className="ticket__divider inside-ticket__body">
                                                    <div className="ticket__notch ticket__notch--left"> </div>
                                                    <div className="ticket__notch ticket__notch--right"> </div>
                                                </div>
                                                {!isLoading && (
                                                    <Button
                                                        aria-expanded={showMoreData ? "true" : "false"}
                                                        className="more-button"
                                                        label={
                                                            showMoreData ? "transfers.showLess" : "transfers.showMore"
                                                        }
                                                        bsStyle="link"
                                                        onClick={toggleShowMore}
                                                        image={
                                                            showMoreData
                                                                ? "figma/icon/arrow-up.svg"
                                                                : "figma/icon/arrow-down.svg"
                                                        }
                                                    />
                                                )}
                                            </div>
                                            <div className={`view-more-data ${showMoreData ? "expand " : "collapse"}`}>
                                                <h2 className="col-span-full">
                                                    {i18nUtils.get("", "Datos adicionales")}
                                                </h2>

                                                <CustomData
                                                    titleText={i18nUtils.get(
                                                        "vep.payment.data.debit.account",
                                                        "Cuenta débito",
                                                    )}
                                                    titleClass="text-Primary-primary-color"
                                                    valueText={transaction?.data?.accountLabel || "-"}
                                                />

                                                <CustomData
                                                    dataClass="align-content-end"
                                                    titleText={i18nUtils.get(
                                                        "vep.payment.total.items",
                                                        "Cantidad de VEPs",
                                                    )}
                                                    valueText={transaction?.data?.vepList.length}
                                                />

                                                <div className="ticket__divider inside-ticket__body">
                                                    <div className="ticket__notch ticket__notch--left"> </div>
                                                    <div className="ticket__notch ticket__notch--right"> </div>
                                                </div>
                                                <h2>{i18nUtils.get("", "Detalle del lote")}</h2>
                                                <GridTable
                                                    className={`grid-table vep-historic-table ${
                                                        !isPendingTransaction
                                                            ? "fromTransactionPage"
                                                            : "fromPendingTransactionPage"
                                                    } tablelist col-span-full`}>
                                                    {isDesktop && (
                                                        <GridTable.Header className="">
                                                            <GridTable.Container>
                                                                <GridTable.Data>
                                                                    <I18n component="span" id="vep.payment.number" />
                                                                </GridTable.Data>
                                                                <GridTable.Data alignX="center">
                                                                    <I18n component="span" id="vep.payment.dueDate" />
                                                                </GridTable.Data>
                                                                <GridTable.Data alignX="center">
                                                                    <I18n
                                                                        component="span"
                                                                        id="vep.payment.cuitTaxpayer"
                                                                    />
                                                                </GridTable.Data>
                                                                <GridTable.Data alignX="center">
                                                                    <I18n
                                                                        component="span"
                                                                        id="vep.payment.description"
                                                                    />
                                                                </GridTable.Data>
                                                                <GridTable.Data alignX="center">
                                                                    <I18n component="span" id="vep.payment.amount" />
                                                                </GridTable.Data>
                                                                <GridTable.Data alignX="center">
                                                                    <I18n component="span" id="vep.payment.status" />
                                                                </GridTable.Data>
                                                                {!isPendingTransaction && (
                                                                    <GridTable.Data alignX="center">
                                                                        <I18n component="span" id="vep.paymentId" />
                                                                    </GridTable.Data>
                                                                )}
                                                            </GridTable.Container>
                                                        </GridTable.Header>
                                                    )}
                                                    <div className="grid-table-body">
                                                        {vepList &&
                                                            vepList.length > 0 &&
                                                            vepList.map((vep) => (
                                                                <GridTable.Container>
                                                                    <GridTable.Data
                                                                        className="align-items-center ellipsis-vep"
                                                                        columnStart={isDesktop ? 1 : 1}
                                                                        rowStart={isDesktop ? 1 : 1}
                                                                        alignX={isDesktop ? "center" : "flex-start"}>
                                                                        <CustomData
                                                                            titleText="vep.payment.number"
                                                                            titleTextType="I18N"
                                                                            valueText={vep.id}
                                                                            valueTextType="TEXT"
                                                                        />
                                                                    </GridTable.Data>
                                                                    <GridTable.Data
                                                                        className="align-items-center"
                                                                        columnStart={isDesktop ? 2 : 1}
                                                                        rowStart={isDesktop ? 1 : 2}
                                                                        alignX={isDesktop ? "center" : "flex-start"}>
                                                                        <CustomData
                                                                            titleText="vep.payment.dueDate"
                                                                            titleTextType="I18N"
                                                                            valueText={vep.expirationDate}
                                                                            valueTextType="DATE"
                                                                        />
                                                                    </GridTable.Data>
                                                                    <GridTable.Data
                                                                        className="align-items-center"
                                                                        columnStart={isDesktop ? 3 : 1}
                                                                        rowStart={isDesktop ? 1 : 3}
                                                                        alignX={isDesktop ? "center" : "flex-start"}>
                                                                        {isDesktop ? (
                                                                            <CustomText
                                                                                text={vep.cuitTaxpayer}
                                                                                textType="TEXT"
                                                                            />
                                                                        ) : (
                                                                            <CustomData
                                                                                titleText="vep.payment.cuitTaxpayer"
                                                                                titleTextType="I18N"
                                                                                valueText={vep.cuitTaxpayer}
                                                                                valueTextType="TEXT"
                                                                            />
                                                                        )}
                                                                    </GridTable.Data>
                                                                    <GridTable.Data
                                                                        className="align-items-center"
                                                                        columnStart={isDesktop ? 4 : 1}
                                                                        rowStart={isDesktop ? 1 : 4}
                                                                        alignX={isDesktop ? "center" : "flex-start"}>
                                                                        {isDesktop ? (
                                                                            <CustomText
                                                                                text={vep.description}
                                                                                textType="TEXT"
                                                                            />
                                                                        ) : (
                                                                            <CustomData
                                                                                titleText="vep.payment.description"
                                                                                titleTextType="I18N"
                                                                                valueText={vep.description}
                                                                                valueTextType="TEXT"
                                                                            />
                                                                        )}
                                                                    </GridTable.Data>
                                                                    <GridTable.Data
                                                                        className="align-items-center vep-amount"
                                                                        columnStart={isDesktop ? 5 : 1}
                                                                        rowStart={isDesktop ? 1 : 5}
                                                                        alignX={isDesktop ? "center" : "flex-start"}>
                                                                        {isDesktop ? (
                                                                            <FormattedAmount
                                                                                currency={vep.currency}
                                                                                quantity={vep.amount}
                                                                            />
                                                                        ) : (
                                                                            <CustomData
                                                                                titleText="vep.payment.amount"
                                                                                titleTextType="I18N"
                                                                                valueText={
                                                                                    <FormattedAmount
                                                                                        currency={vep.currency}
                                                                                        quantity={vep.amount}
                                                                                    />
                                                                                }
                                                                                valueTextType="TEXT"
                                                                            />
                                                                        )}
                                                                    </GridTable.Data>
                                                                    <GridTable.Data
                                                                        className="tags-wrapper align-items-center ellipsis-vep"
                                                                        columnStart={isDesktop ? 6 : 2}
                                                                        rowStart={1}
                                                                        alignX={isDesktop ? "center" : "end"}>
                                                                        {isPendingTransaction ? (
                                                                            <InfoTag
                                                                                type="info"
                                                                                message={i18nUtils.get(
                                                                                    "vep.payment.status.PENDIENTE_PAGO",
                                                                                    "Pendiente de pago",
                                                                                )}
                                                                                tagBackground="warning"
                                                                            />
                                                                        ) : vep.status === "PAGADO" ? (
                                                                            <InfoTag
                                                                                type="info"
                                                                                message={i18nUtils.get(
                                                                                    "vep.payment.status.PAGADO",
                                                                                    "Pagado",
                                                                                )}
                                                                                tagBackground="success"
                                                                            />
                                                                        ) : (
                                                                            <InfoTag
                                                                                type="info"
                                                                                message={i18nUtils.get("", "Error")}
                                                                                tagBackground="error"
                                                                            />
                                                                        )}
                                                                    </GridTable.Data>
                                                                    {!isPendingTransaction && (
                                                                        <GridTable.Data
                                                                            className="align-items-center"
                                                                            columnStart={isDesktop ? 7 : 1}
                                                                            rowStart={isDesktop ? 1 : 7}
                                                                            alignX={
                                                                                isDesktop ? "center" : "flex-start"
                                                                            }>
                                                                            {isDesktop ? (
                                                                                <CustomText
                                                                                    text={vep.sequenceNumber || "-"}
                                                                                    textType="TEXT"
                                                                                />
                                                                            ) : (
                                                                                !isPendingTransaction && (
                                                                                    <CustomData
                                                                                        titleText="vep.paymentId"
                                                                                        titleTextType="I18N"
                                                                                        valueText={vep.sequenceNumber}
                                                                                        valueTextType="TEXT"
                                                                                    />
                                                                                )
                                                                            )}
                                                                        </GridTable.Data>
                                                                    )}
                                                                </GridTable.Container>
                                                            ))}
                                                    </div>
                                                </GridTable>
                                                <TransactionSign
                                                    hasVaucher
                                                    isDesktop={isDesktop}
                                                    noDate
                                                    isSmall
                                                    creationTime
                                                    showMoreData={showMoreData}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className="ticket__footer"
                                            data-copytext={i18nUtils.get("global.ticketCopyText")}
                                        />
                                    </div>
                                </div>
                            </div>
                        </TransactionCard>
                    </div>
                </div>
                {!isBackoffice && (
                    <Row className="form-actions">
                        {!isCancellingTransaction ? (
                            <>
                                <Col
                                    xs={12}
                                    md={4}
                                    mdOffset={transaction.idTransactionStatus !== transactionStatus.PROCESSING ? 2 : 4}>
                                    {transaction.idTransactionStatus !== transactionStatus.PENDING && (
                                        <Button
                                            onClick={() => dispatch(push("/desktop"))}
                                            label="forms.transaction.ticket.actions.backToHome"
                                            className={`btn${
                                                transaction.idTransactionStatus === transactionStatus.FAILED ||
                                                transaction.idTransactionStatus === transactionStatus.FINISHED ||
                                                transaction.idTransactionStatus === transactionStatus.CANCELLED
                                                    ? " btn-outline"
                                                    : " btn-primary"
                                            }`}
                                        />
                                    )}
                                    {transaction.idTransactionStatus === transactionStatus.PENDING && (
                                        <Button
                                            className={`btn${showSignButton ? " btn-outline" : " btn-primary"}`}
                                            label="forms.cancelTransaction.link"
                                            onClick={() => dispatch(formActions.changeCancelTransaction(true))}
                                        />
                                    )}
                                </Col>

                                <Col xs={12} md={4}>
                                    {(transaction.idTransactionStatus === transactionStatus.FINISHED ||
                                        transaction.idTransactionStatus === transactionStatus.FAILED ||
                                        transaction.idTransactionStatus === transactionStatus.CANCELLED) && (
                                        <Button
                                            className="btn btn-primary"
                                            image="images/figma/icon/download.svg"
                                            label="global.download"
                                            onClick={handleDownload}
                                        />
                                    )}

                                    {showSignButton && (
                                        <Button
                                            onClick={handleSignTransaction}
                                            label="forms.signTransaction.link"
                                            className="btn btn-primary"
                                        />
                                    )}
                                </Col>
                                {transaction.idTransactionStatus === transactionStatus.FINISHED && !isDesktop && (
                                    <Col xs={12} md={4}>
                                        <Button
                                            className="btn btn-outline"
                                            image="images/figma/icon/share.svg"
                                            label="global.share"
                                            onClick={() => {
                                                handleShareMobile();
                                            }}
                                        />
                                    </Col>
                                )}
                            </>
                        ) : (
                            <Form className="above-the-fold container--layout">
                                <Row>
                                    <Col xs={12} md={4} mdOffset={2}>
                                        <Field
                                            idForm="form.credential"
                                            name="password"
                                            labelText="form.credential.password.label"
                                            placeholderText="Ej: Ju@n_123"
                                            component={Credential}
                                            type="password"
                                        />
                                    </Col>
                                    <Col xs={12} md={4} className="align-items-end">
                                        <div className="form-group hidden-label">
                                            <div className="button-group">
                                                <Button
                                                    block
                                                    type="submit"
                                                    label="global.send"
                                                    bsStyle="primary"
                                                    loading={fetchingCancel}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </Row>
                )}
            </MainContainer>
        </>
    );
};

VepPaymentTransactionPage.propTypes = {
    isDesktop: bool.isRequired,
    isCancellingTransaction: bool.isRequired,
    fetchingCancel: bool.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    transaction: shape({}).isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    idTransaction: string.isRequired,
};

const mapStateToProps = (state) => ({
    isCancellingTransaction: formSelectors.getIsCancellingTransaction(state),
    fetchingCancel: formSelectors.getFetching(state),
    transaction: formSelectors.getTransaction(state),
    idTransaction: formSelectors.getTransaction(state).idTransaction,
});

export default compose(
    connect(mapStateToProps),
    withFormik({
        validateOnChange: false,
        validateOnBlur: false,
        mapPropsToValues: (props) => ({
            id: props.match.params.id,
            transaction: props.transaction,
            isCancellingTransaction: props.isCancellingTransaction,
            password: "",
        }),
        validationSchema: (props) =>
            Yup.object().shape({
                password: props.isCancellingTransaction
                    ? Yup.string().required(i18nUtils.get("deposits.request.required"))
                    : Yup.string().notRequired(),
            }),
        handleSubmit: ({ password }, formikBag) => {
            const { dispatch, isCancellingTransaction } = formikBag.props;
            if (isCancellingTransaction) {
                dispatch(formActions.cancelTransaction({ credentials: { password, otp: "" }, formikBag }));
            }
        },
    }),
)(VepPaymentTransactionPage);
